/* ----------------------------------------------------------------------------
   MOBILE NAVIGATION DRAWER — enhancement layer (snippets/mobile-nav.liquid)
   ----------------------------------------------------------------------------
   The drawer mechanics (<header-sidebar>: focus trap, Esc, overlay, two-level
   slide animation) come from theme.js. This element only adds behavior the
   redesign needs on top:

   - toggles a [data-sub-level] attribute on the drawer while a sub-panel is
     open, so CSS can swap the ✕ close button for the ‹ back button
   - wires the header back button (closes the open sub-panel, mirroring the
     theme's own [data-action="close-panel"] handler)
   - cart icon handoff: closes the drawer first, then opens the cart drawer
     (falls back to the /cart link when no cart drawer is present)
---------------------------------------------------------------------------- */
(() => {
  class MobileNavHeader extends HTMLElement {
    connectedCallback() {
      this.sidebar = this.closest('header-sidebar');

      if (!this.sidebar) {
        return;
      }

      this.panel = this.sidebar.querySelector('header-sidebar-collapsible-panel');

      this.querySelector('[data-mobile-nav-back]')?.addEventListener('click', () => this.closeSubPanel());
      this.querySelector('[data-mobile-nav-cart]')?.addEventListener('click', (event) => this.openCartDrawer(event));
      // The search field lives in the main panel (not the header), so query the whole sidebar.
      this.sidebar.querySelector('[data-mobile-nav-search]')?.addEventListener('click', (event) => this.openSearchDrawer(event));

      // Same-page anchor links (e.g. the /#newsletter footer item): a plain hash change would
      // leave the drawer open and the page scroll-locked — close the drawer first, then scroll.
      this.sidebar.addEventListener('click', (event) => this.handleAnchorLink(event));

      if (this.panel) {
        this.observer = new MutationObserver(() => this.syncLevel());
        this.observer.observe(this.panel, { attributes: true, attributeFilter: ['aria-activedescendant'] });
        this.syncLevel();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    syncLevel() {
      this.sidebar.toggleAttribute('data-sub-level', this.panel.hasAttribute('aria-activedescendant'));
    }

    handleAnchorLink(event) {
      const link = event.target.closest('a[href*="#"]');

      if (!link || event.defaultPrevented) {
        return;
      }

      const url = new URL(link.href, window.location.href);

      // only same-page anchors — cross-page links navigate normally
      if (!url.hash || url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
        return;
      }

      const target = document.querySelector(url.hash);

      if (!target) {
        return;
      }

      event.preventDefault();

      const scrollToTarget = () => {
        history.pushState(null, '', url.hash);
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      const hidePromise = this.sidebar.hide();

      if (hidePromise && typeof hidePromise.then === 'function') {
        hidePromise.then(scrollToTarget);
      } else {
        scrollToTarget();
      }
    }

    closeSubPanel() {
      if (!this.panel) {
        return;
      }

      // Removing aria-activedescendant triggers the panel's own hide/slide animation
      this.panel.removeAttribute('aria-activedescendant');

      this.sidebar
        .querySelectorAll('.header-sidebar__main-panel [aria-controls][aria-expanded]')
        .forEach((button) => button.setAttribute('aria-expanded', 'false'));

      // Bring the main panel back in (same as the theme's close-panel handler)
      this.sidebar.revealItems?.(true);
    }

    openCartDrawer(event) {
      const cartDrawer = document.getElementById('cart-drawer');

      if (!cartDrawer || typeof cartDrawer.show !== 'function') {
        return; // no cart drawer on this page — follow the /cart link
      }

      event.preventDefault();

      const showCart = () => cartDrawer.show();
      const hidePromise = this.sidebar.hide();

      if (hidePromise && typeof hidePromise.then === 'function') {
        hidePromise.then(showCart);
      } else {
        showCart();
      }
    }

    openSearchDrawer(event) {
      const searchDrawer = document.getElementById('search-drawer');

      if (!searchDrawer || typeof searchDrawer.show !== 'function') {
        return; // no search drawer on this page — follow the /search link
      }

      event.preventDefault();

      // Close this menu (slides out left) and open the search drawer (slides in right) at the
      // same time — opposite sides, so the two panels don't overlap during the transition.
      const hidePromise = this.sidebar.hide();
      searchDrawer.show();

      // The menu's focus trap returns focus to its opener once it finishes closing; pull focus
      // back into the search field afterwards so the keyboard is ready to type.
      const field = searchDrawer.querySelector('[data-search-field]');
      const focusField = () => field?.focus({ preventScroll: true });

      if (hidePromise && typeof hidePromise.then === 'function') {
        hidePromise.then(focusField);
      } else {
        focusField();
      }
    }
  }

  if (!window.customElements.get('mobile-nav-header')) {
    window.customElements.define('mobile-nav-header', MobileNavHeader);
  }
})();
